// src/components/SchedulePickerModal/SchedulePickerModal.jsx
import React, { useMemo } from 'react';
import CalendarPicker from '../Calendar/CalendarPicker';
import { generateSlotsDay, humanLabel, toISODate, format12h } from '../../utils/schedule';
import './SchedulePickerModal.css';
import { X } from 'lucide-react';

export default function SchedulePickerModal({
  open,
  dateISO,          // "YYYY-MM-DD" | ""
  timeHHmm,         // "HH:mm" | ""
  onChangeDate,
  onChangeTime,
  onClose,
  onSave,
  minDateISO,
  title = 'Schedule',
}) {
  // compute today's ISO for comparisons once per render
  const { todayISO, nowHour } = useMemo(() => {
    const now = new Date();
    const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { todayISO: toISODate(t), nowHour: now.getHours() };
  }, []);

  const slots = useMemo(() => generateSlotsDay(), []);
  const hasSlots = slots.length > 0;
  const labelText = !dateISO
    ? 'Pick a date'
    : !timeHHmm
      ? `Pick a time for ${new Date(dateISO).toLocaleDateString([], { dateStyle: 'medium' })}`
      : `Selected: ${humanLabel(dateISO, timeHHmm)}`;

  if (!open) return null;

  return (
    <div className="modal-backdrop modal-backdrop-inner" onClick={onClose}>
      <div className="modal-panel" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="schedule-two-col">
          {/* Calendar side */}
          <CalendarPicker
            value={dateISO}
            onChange={(iso) => { onChangeDate(iso); onChangeTime(''); }}
            minDateISO={minDateISO}
          />

          {/* Time side */}
          <div className="time-panel">
            <div className="schedule-row">
              <label className="schedule-label">{labelText}</label>
            </div>

            <div className="time-grid">
              {slots.map(t => {
                const hour = parseInt(t.slice(0, 2), 10);
                const disableForToday = dateISO === todayISO && hour <= nowHour;
                const selected = timeHHmm === t;

                return (
                  <button
                    key={t}
                    type="button"
                    className={`time-chip ${selected ? 'selected' : ''}`}
                    onClick={() => !disableForToday && onChangeTime(t)}
                    disabled={disableForToday}
                    aria-pressed={selected}
                  >
                    {format12h(t)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="modal-actions schedule-module-actions">
          <button className="button action-button action-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="button action-button"
            disabled={!dateISO || !timeHHmm}
            onClick={onSave}
          >
            Schedule Session
          </button>
        </div>
      </div>
    </div>
  );
}